/**
 * Service Request Form
 * Handles form submission to App Proxy endpoint for measurement service requests.
 * Also performs Dutch postcode + house number lookup against the carpetright-app proxy.
 */

const POSTCODE_REGEX = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/;
const HOUSE_NUMBER_REGEX = /^[1-9][0-9]{0,4}$/;

class ServiceRequestForm extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._isSubmitting = false;
    this._initialized = false;
    this._lookupAbortController = null;
    this._lastLookupQuery = null;
    this._inflightLookup = null;
  }

  connectedCallback() {
    if (this._initialized) {
      return;
    }

    this._initialized = true;
    this._abortController = new AbortController();
    this._initElements();
    this._bindEvents();
  }

  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
    }
    if (this._lookupAbortController) {
      this._lookupAbortController.abort();
    }
    this._initialized = false;
  }

  get endpoint() {
    return this.dataset.endpoint || '/apps/service-request';
  }

  _initElements() {
    this._statusContainer = this.querySelector('[data-status-container]');
    this._successMessage = this.querySelector('[data-success-message]');
    this._errorMessage = this.querySelector('[data-error-message]');
    this._formFields = this.querySelector('[data-form-fields]');
    this._submitContainer = this.querySelector('[data-submit-container]');
    this._submitButton = this._submitContainer?.querySelector('button[type="submit"]');
    this._businessNameField = this.querySelector('[data-business-name-field]');
    this._lookupStatusEl = this.querySelector('[data-lookup-status]');

    this._inputs = {
      customer_type: this.querySelector('select[name="customer_type"]'),
      business_name: this.querySelector('input[name="business_name"]'),
      name: this.querySelector('input[name="name"]'),
      email: this.querySelector('input[name="email"]'),
      phone: this.querySelector('input[name="phone"]'),
      postcode: this.querySelector('input[name="postcode"]'),
      house_number: this.querySelector('input[name="house_number"]'),
      house_number_addition: this.querySelector('input[name="house_number_addition"]'),
      street: this.querySelector('input[name="street"]'),
      city: this.querySelector('input[name="city"]'),
      country: this.querySelector('select[name="country"]'),
      service_type: this.querySelector('select[name="service_type"]'),
      preferred_date: this.querySelector('input[name="preferred_date"]'),
      notes: this.querySelector('textarea[name="notes"]')
    };
  }

  _bindEvents() {
    const signal = this._abortController.signal;

    if (this._submitButton) {
      this._submitButton.addEventListener('click', (event) => {
        event.preventDefault();
        this._handleSubmit();
      }, { signal });
    }

    if (this._inputs.customer_type) {
      this._inputs.customer_type.addEventListener('change', () => {
        this._handleCustomerTypeChange();
      }, { signal });
    }

    ['postcode', 'house_number', 'house_number_addition'].forEach((name) => {
      const el = this._inputs[name];
      if (!el) return;
      el.addEventListener('blur', () => this._maybeLookup(), { signal });
    });

    Object.values(this._inputs).forEach((input) => {
      if (input && input.tagName !== 'TEXTAREA') {
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this._handleSubmit();
          }
        }, { signal });
      }
    });
  }

  _handleCustomerTypeChange() {
    const customerType = this._inputs.customer_type?.value;
    const isBusiness = customerType === 'business';

    if (this._businessNameField) {
      this._businessNameField.hidden = !isBusiness;
    }

    if (this._inputs.business_name) {
      this._inputs.business_name.required = isBusiness;
      if (!isBusiness) {
        this._inputs.business_name.value = '';
      }
    }
  }

  _maybeLookup() {
    const postcode = (this._inputs.postcode?.value || '').trim();
    const houseNumber = (this._inputs.house_number?.value || '').trim();
    const addition = (this._inputs.house_number_addition?.value || '').trim();

    if (!POSTCODE_REGEX.test(postcode) || !HOUSE_NUMBER_REGEX.test(houseNumber)) {
      // Abort any in-flight lookup so it cannot land stale data into the now-invalid form
      if (this._lookupAbortController) {
        this._lookupAbortController.abort();
        this._lookupAbortController = null;
      }
      this._lastLookupQuery = null;
      this._setLookupStatus(null);
      return null;
    }

    const key = `${postcode.toUpperCase().replace(/\s+/g, '')}|${houseNumber}|${addition}`;
    if (this._lastLookupQuery === key) return this._inflightLookup;
    this._lastLookupQuery = key;

    if (this._lookupAbortController) {
      this._lookupAbortController.abort();
    }
    this._lookupAbortController = new AbortController();
    const controller = this._lookupAbortController;

    this._setLookupStatus('searching');

    const lookupPromise = (async () => {
      try {
        const res = await fetch(`${this.endpoint}/postcode-lookup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            postcode,
            house_number: houseNumber,
            addition: addition || undefined
          }),
          signal: controller.signal
        });

        if (res.status === 404) {
          this._setLookupStatus('not_found');
          return;
        }

        const data = await res.json().catch(() => null);
        if (!res.ok || !data || !data.ok) {
          this._setLookupStatus('error');
          return;
        }

        if (this._inputs.street && data.street) {
          this._inputs.street.value = data.street;
        }
        if (this._inputs.city && data.city) {
          this._inputs.city.value = data.city;
        }
        this._setLookupStatus(null);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this._setLookupStatus('error');
      }
    })();

    this._inflightLookup = lookupPromise;
    lookupPromise.finally(() => {
      if (this._inflightLookup === lookupPromise) {
        this._inflightLookup = null;
      }
    });

    return lookupPromise;
  }

  _setLookupStatus(state) {
    if (!this._lookupStatusEl) return;
    const states = this._lookupStatusEl.querySelectorAll('[data-lookup-state]');
    states.forEach((el) => {
      el.hidden = el.dataset.lookupState !== state;
    });
    this._lookupStatusEl.hidden = state === null || state === undefined;
  }

  _validateForm() {
    const requiredFields = ['customer_type', 'name', 'email', 'phone', 'postcode', 'house_number', 'street', 'city', 'country', 'service_type'];
    let isValid = true;
    let firstInvalidField = null;

    requiredFields.forEach((fieldName) => {
      const input = this._inputs[fieldName];
      if (!input) return;

      const value = input.value.trim();
      const isFieldValid = value !== '';

      if (!isFieldValid && !firstInvalidField) {
        firstInvalidField = input;
      }

      if (!isFieldValid) {
        isValid = false;
      }
    });

    const customerType = this._inputs.customer_type?.value;
    if (customerType === 'business') {
      const businessName = this._inputs.business_name?.value.trim();
      if (!businessName) {
        isValid = false;
        if (!firstInvalidField) {
          firstInvalidField = this._inputs.business_name;
        }
      }
    }

    if (this._inputs.email) {
      const emailValue = this._inputs.email.value.trim();
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailValue && !emailPattern.test(emailValue)) {
        isValid = false;
        if (!firstInvalidField) {
          firstInvalidField = this._inputs.email;
        }
      }
    }

    if (!isValid && firstInvalidField) {
      firstInvalidField.focus();
      firstInvalidField.reportValidity();
    }

    return isValid;
  }

  _getFormData() {
    const data = {};
    const skip = new Set(['street', 'house_number', 'house_number_addition']);

    Object.entries(this._inputs).forEach(([key, input]) => {
      if (input && !skip.has(key)) {
        data[key] = input.value.trim();
      }
    });

    const street = this._inputs.street?.value.trim() || '';
    const houseNumber = this._inputs.house_number?.value.trim() || '';
    const addition = this._inputs.house_number_addition?.value.trim() || '';
    const numberPart = addition ? `${houseNumber} ${addition}` : houseNumber;
    data.address = [street, numberPart].filter((p) => p && p.length > 0).join(' ').trim();

    // Normalise Dutch postcode to the canonical "1234 AB" form (uppercase, single space).
    // Leaves non-matching input unchanged so server-side validation can still surface the real error.
    if (data.postcode) {
      data.postcode = data.postcode.toUpperCase().replace(/^(\d{4})\s*([A-Z]{2})$/, '$1 $2');
    }

    return data;
  }

  async _handleSubmit() {
    if (this._isSubmitting) return;

    // Wait for any in-flight postcode lookup so street + city are populated before validation
    if (this._inflightLookup) {
      try { await this._inflightLookup; } catch (_) { /* ignored - lookup errors are surfaced inline */ }
    }

    if (!this._validateForm()) return;

    this._isSubmitting = true;
    this._setLoadingState(true);
    this._hideMessages();

    const formData = this._getFormData();

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
        signal: this._abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        this._pushGenerateLead(formData);
        this._showSuccess();
      } else {
        this._showError(result.message);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Service request form submission error:', error);
      this._showError();
    } finally {
      this._isSubmitting = false;
      this._setLoadingState(false);
    }
  }

  _normalizePhoneNL(phone) {
    if (!phone) return '';
    var cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
    if (cleaned.indexOf('0031') === 0) cleaned = '+31' + cleaned.substring(4);
    else if (cleaned.indexOf('00') === 0) cleaned = '+' + cleaned.substring(2);
    else if (cleaned.indexOf('0') === 0) cleaned = '+31' + cleaned.substring(1);
    else if (cleaned.indexOf('+') !== 0) cleaned = '+31' + cleaned;
    return cleaned;
  }

  _splitName(fullName) {
    if (!fullName) return { first_name: '', last_name: '' };
    var trimmed = fullName.trim();
    var lastSpace = trimmed.lastIndexOf(' ');
    if (lastSpace === -1) return { first_name: trimmed, last_name: '' };
    return {
      first_name: trimmed.substring(0, lastSpace),
      last_name: trimmed.substring(lastSpace + 1)
    };
  }

  _pushGenerateLead(formData) {
    try {
      var names = this._splitName(formData.name);
      var normalizedPhone = this._normalizePhoneNL(formData.phone);
      var attribution = (window.CRAttribution && window.CRAttribution.capture)
        ? window.CRAttribution.capture()
        : {};

      var SERVICE_VALUE_EUR = { floor: 700, stairs: 400, floor_stairs: 1000 };
      var CUSTOMER_MULTIPLIER = { business: 1.25, customer: 1.0 };
      var baseValue = SERVICE_VALUE_EUR[formData.service_type] || 250;
      var multiplier = CUSTOMER_MULTIPLIER[formData.customer_type] || 1.0;
      var leadValue = Math.round(baseValue * multiplier);

      var payload = {
        event: 'generate_lead_stape',
        value: leadValue,
        currency: 'EUR',
        lead_source: 'home_advice_form',
        form_name: 'home_advice_form',
        service_type: formData.service_type || '',
        customer_type: formData.customer_type || '',
        user_data: {
          email_address: formData.email,
          phone_number: normalizedPhone,
          first_name: names.first_name,
          last_name: names.last_name,
          street: formData.address || '',
          city: formData.city || '',
          postal_code: formData.postcode || '',
          country: formData.country || 'NL'
        }
      };
      Object.keys(attribution).forEach(function (k) { payload[k] = attribution[k]; });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {
      console.error('generate_lead tracking error:', e);
    }
  }

  _setLoadingState(isLoading) {
    if (this._submitButton) {
      this._submitButton.disabled = isLoading;
      this._submitButton.setAttribute('aria-busy', isLoading.toString());
    }
  }

  _hideMessages() {
    if (this._statusContainer) {
      this._statusContainer.hidden = true;
    }
    if (this._successMessage) {
      this._successMessage.hidden = true;
    }
    if (this._errorMessage) {
      this._errorMessage.hidden = true;
    }
  }

  _showSuccess() {
    if (this._formFields) {
      this._formFields.hidden = true;
    }
    if (this._submitContainer) {
      this._submitContainer.hidden = true;
    }
    if (this._statusContainer) {
      this._statusContainer.hidden = false;
    }
    if (this._successMessage) {
      this._successMessage.hidden = false;
      this._successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  _showError(message) {
    if (this._statusContainer) {
      this._statusContainer.hidden = false;
    }
    if (this._errorMessage) {
      this._errorMessage.hidden = false;
      if (message) {
        const bannerContent = this._errorMessage.querySelector('.banner');
        if (bannerContent) {
          const textNode = Array.from(bannerContent.childNodes).find(
            (node) => node.nodeType === Node.TEXT_NODE || node.tagName !== 'SVG'
          );
          if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            textNode.textContent = message;
          }
        }
      }
      this._errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}

if (!window.customElements.get('service-request-form')) {
  window.customElements.define('service-request-form', ServiceRequestForm);
}
